import * as Location from 'expo-location';
import { Platform } from 'react-native';
import { logger } from '../utils/logger';

export interface UserLocation {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  altitude: number | null;
  speed: number | null;
  heading: number | null;
  timestamp: number;
}

class LocationService {
  private watchId: Location.LocationSubscription | null = null;
  private lastKnownLocation: UserLocation | null = null;

  async requestPermissions(): Promise<boolean> {
    try {
      // Add a small delay to ensure app is fully initialized
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const { status } = await Location.requestForegroundPermissionsAsync();
      
      if (status !== 'granted') {
        logger.warn('Location permission denied');
        return false;
      }
      
      // Skip background permissions for now to avoid crashes
      // if (Platform.OS === 'ios') {
      //   const { status: bgStatus } = await Location.requestBackgroundPermissionsAsync();
      //   logger.info('iOS background location permission:', bgStatus);
      // }
      
      return true;
    } catch (error) {
      logger.error('Error requesting location permissions', error);
      return false;
    }
  }

  async getCurrentLocation(): Promise<UserLocation | null> {
    try {
      const hasPermission = await this.requestPermissions();
      if (!hasPermission) {
        return null;
      }

      logger.info('Getting current location...');
      
      const location = await Location.getCurrentPositionAsync({
        accuracy: Location.Accuracy.BestForNavigation,
        timeInterval: 1000,
        mayShowUserSettingsDialog: true,
      });

      // Fix for Western hemisphere - ensure negative longitude for Americas
      // Corpus Christi area should be around -97 longitude
      let fixedLongitude = location.coords.longitude;
      if (fixedLongitude > 0 && fixedLongitude < 180) {
        // If we get a positive longitude that should be in Western hemisphere
        fixedLongitude = -fixedLongitude;
      }

      const userLocation: UserLocation = {
        latitude: location.coords.latitude,
        longitude: fixedLongitude,
        accuracy: location.coords.accuracy,
        altitude: location.coords.altitude,
        speed: location.coords.speed,
        heading: location.coords.heading,
        timestamp: location.timestamp,
      };

      this.lastKnownLocation = userLocation;
      
      logger.info('Location obtained', {
        lat: userLocation.latitude.toFixed(6),
        lon: userLocation.longitude.toFixed(6),
        accuracy: userLocation.accuracy?.toFixed(0),
      });

      return userLocation;
    } catch (error) {
      logger.error('Error getting current location', error);
      
      // Try to get last known location as fallback
      try {
        const lastKnown = await Location.getLastKnownPositionAsync();
        if (lastKnown) {
          // Fix longitude for Western hemisphere
          let fixedLongitude = lastKnown.coords.longitude;
          if (fixedLongitude > 0 && fixedLongitude < 180) {
            fixedLongitude = -fixedLongitude;
          }

          const fallbackLocation: UserLocation = {
            latitude: lastKnown.coords.latitude,
            longitude: fixedLongitude,
            accuracy: lastKnown.coords.accuracy,
            altitude: lastKnown.coords.altitude,
            speed: lastKnown.coords.speed,
            heading: lastKnown.coords.heading,
            timestamp: lastKnown.timestamp,
          };
          
          logger.info('Using last known location', {
            lat: fallbackLocation.latitude.toFixed(6),
            lon: fallbackLocation.longitude.toFixed(6),
            age: `${Math.round((Date.now() - fallbackLocation.timestamp) / 1000)  }s ago`,
          });
          
          return fallbackLocation;
        }
      } catch (fallbackError) {
        logger.error('Error getting last known location', fallbackError);
      }
      
      return null;
    }
  }

  async watchLocation(
    callback: (location: UserLocation) => void,
    options?: {
      accuracy?: Location.Accuracy;
      distanceInterval?: number;
      timeInterval?: number;
    }
  ): Promise<void> {
    try {
      const hasPermission = await this.requestPermissions();
      if (!hasPermission) {
        return;
      }

      // Stop existing watch if any
      if (this.watchId) {
        this.watchId.remove();
      }

      logger.info('Starting location watch');

      this.watchId = await Location.watchPositionAsync(
        {
          accuracy: options?.accuracy || Location.Accuracy.BestForNavigation,
          distanceInterval: options?.distanceInterval || 10, // Update every 10 meters
          timeInterval: options?.timeInterval || 5000, // Update every 5 seconds
        },
        (location) => {
          // Fix longitude for Western hemisphere
          let fixedLongitude = location.coords.longitude;
          if (fixedLongitude > 0 && fixedLongitude < 180) {
            fixedLongitude = -fixedLongitude;
          }

          const userLocation: UserLocation = {
            latitude: location.coords.latitude,
            longitude: fixedLongitude,
            accuracy: location.coords.accuracy,
            altitude: location.coords.altitude,
            speed: location.coords.speed,
            heading: location.coords.heading,
            timestamp: location.timestamp,
          };

          this.lastKnownLocation = userLocation;
          
          logger.debug('Location update', {
            lat: userLocation.latitude.toFixed(6),
            lon: userLocation.longitude.toFixed(6),
            accuracy: userLocation.accuracy?.toFixed(0),
          });

          callback(userLocation);
        }
      );
    } catch (error) {
      logger.error('Error watching location', error);
    }
  }

  stopWatchingLocation(): void {
    if (this.watchId) {
      logger.info('Stopping location watch');
      this.watchId.remove();
      this.watchId = null;
    }
  }

  getLastKnownLocation(): UserLocation | null {
    return this.lastKnownLocation;
  }

  // Calculate distance between two coordinates in meters
  static calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  // Check if location services are enabled
  static async isLocationServicesEnabled(): Promise<boolean> {
    try {
      const enabled = await Location.hasServicesEnabledAsync();
      if (!enabled) {
        logger.warn('Location services are disabled');
      }
      return enabled;
    } catch (error) {
      logger.error('Error checking location services', error);
      return false;
    }
  }
}

export const locationService = new LocationService();
export { LocationService };